import {
  ButtonHTMLAttributes,
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Tooltip } from '../Tooltip';
import styles from './Chip.module.css';

export interface ChipProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'onSelect' | 'children'
> {
  isSelected?: boolean;
  value: string;
  onSelect?: (value: string) => void;
  label: string;
  count: number;
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(
  (
    { isSelected = false, value, onSelect, onClick, label, count, ...props },
    ref
  ) => {
    const labelRef = useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useLayoutEffect(() => {
      const node = labelRef.current;
      if (!node) {
        return undefined;
      }

      const measure = () => {
        setIsOverflowing(node.scrollWidth > node.clientWidth);
      };

      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }, [label]);

    const button = (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={isSelected}
        data-selected={isSelected}
        className={styles.root}
        onClick={(event) => {
          onClick?.(event);
          onSelect?.(value);
        }}
        {...props}
      >
        <span ref={labelRef} className={styles.label}>
          {label}
        </span>
        <span className={styles.count}>{`(${count})`}</span>
      </button>
    );

    return (
      <Tooltip label={label} disabled={!isOverflowing}>
        {button}
      </Tooltip>
    );
  }
);

Chip.displayName = 'FilterChip';
